import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { apiFetch, apiJson } from '../apiClient';
import { EXPENSE_CATEGORY_VALUES } from '../constants/expenseCategories';
import {
  expenseCategoryOptionsForUser,
  factoryExpenseCategoryOptions,
} from '../constants/factoryExpenseCategories';
import { EXPENSE_PAYMENT_CASH, EXPENSE_PAYMENT_LIPA, expensePaymentOptions } from '../constants/expensePayment';
import {
  STAFF_PAY_DAY_WORK,
  STAFF_PAY_PERMANENT,
  staffPayTypeOptions,
} from '../constants/staffPayTypes';
import { useLanguage } from '../i18n/LanguageContext';
import { isBrickFactoryUser, isBarPubUser } from '../utils/businessType';
import { consumeExpensesAddPrefs } from '../utils/navigationFocus';
import { addDays, getLocalDateKey } from '../utils/reportDates';
import './expenses/ExpensesShell.css';

const CATEGORY_META = {
  staff: { icon: '👥', accent: 'staff' },
  office: { icon: '🏭', accent: 'office' },
  boss: { icon: '👤', accent: 'boss' },
};

function buildDatePresets(t, todayKey) {
  return [
    { key: 'today', label: t('exp.add.dateToday'), value: todayKey },
    { key: 'yesterday', label: t('exp.add.dateYesterday'), value: addDays(todayKey, -1) },
    { key: 'day2', label: t('exp.add.dateDay2'), value: addDays(todayKey, -2) },
    { key: 'custom', label: t('exp.add.dateCustom'), value: null },
  ];
}

function formatDateLabel(dateKey, lang) {
  if (!dateKey) return '';
  try {
    const d = new Date(`${dateKey}T12:00:00`);
    const locale = lang === 'sw' ? 'sw-TZ' : undefined;
    return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateKey;
  }
}

function formatTzs(n) {
  return Math.round(Number(n) || 0).toLocaleString('en-TZ');
}

const ExpensesAdd = ({ user, onNavigate }) => {
  const { t, lang } = useLanguage();
  const factory = isBrickFactoryUser(user);
  const barMode = isBarPubUser(user);
  const categoryOptions = useMemo(() => expenseCategoryOptionsForUser(user, lang), [user, lang]);
  const payTypeOptions = useMemo(() => staffPayTypeOptions(lang), [lang]);
  const paymentOptions = useMemo(() => expensePaymentOptions(lang), [lang]);
  const todayKey = getLocalDateKey();
  const datePresets = useMemo(() => buildDatePresets(t, todayKey), [t, todayKey]);

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [datePreset, setDatePreset] = useState('today');
  const [expenseDate, setExpenseDate] = useState(todayKey);
  const [category, setCategory] = useState(EXPENSE_CATEGORY_VALUES.STAFF);
  const [staffPayType, setStaffPayType] = useState(STAFF_PAY_PERMANENT);
  const [cementBagsPay, setCementBagsPay] = useState('');
  const [cementBagRate, setCementBagRate] = useState('');
  const [productionCement, setProductionCement] = useState(null);
  const [loadingProduction, setLoadingProduction] = useState(false);
  const [notes, setNotes] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidencePreview, setEvidencePreview] = useState(null);
  const [evidencePreviewKind, setEvidencePreviewKind] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(EXPENSE_PAYMENT_CASH);
  const [paymentReference, setPaymentReference] = useState('');
  const [phase, setPhase] = useState('idle');
  const [message, setMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const isStaffFactory = factory && category === EXPENSE_CATEGORY_VALUES.STAFF;
  const isDayWork = isStaffFactory && staffPayType === STAFF_PAY_DAY_WORK;

  useLayoutEffect(() => {
    const p = consumeExpensesAddPrefs();
    if (p?.category && ['boss', 'staff', 'office'].includes(p.category)) {
      setCategory(p.category);
    }
  }, []);

  const busy = phase === 'loading';

  const categoryHint = factory
    ? factoryExpenseCategoryOptions(lang).find((o) => o.value === category)?.hint
    : null;

  const dayWorkTotal = useMemo(() => {
    const bags = Number(cementBagsPay);
    const rate = Number(cementBagRate);
    if (!Number.isFinite(bags) || !Number.isFinite(rate) || bags <= 0 || rate <= 0) return 0;
    return Math.round(bags * rate * 100) / 100;
  }, [cementBagsPay, cementBagRate]);

  useEffect(() => {
    if (isDayWork && dayWorkTotal > 0) {
      setAmount(String(dayWorkTotal));
    }
  }, [isDayWork, dayWorkTotal]);

  useEffect(() => {
    return () => {
      if (evidencePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(evidencePreview);
      }
    };
  }, [evidencePreview]);

  const resetEvidence = () => {
    if (evidencePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(evidencePreview);
    }
    setEvidenceFile(null);
    setEvidencePreview(null);
    setEvidencePreviewKind(null);
    setFileKey((k) => k + 1);
  };

  const handleEvidenceChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (evidencePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(evidencePreview);
    }
    setEvidenceFile(file);
    if (!file) {
      setEvidencePreview(null);
      setEvidencePreviewKind(null);
      return;
    }
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (isPdf) {
      setEvidencePreviewKind('pdf');
      setEvidencePreview(URL.createObjectURL(file));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setEvidencePreview(null);
      setEvidencePreviewKind(null);
      return;
    }
    setEvidencePreviewKind('image');
    const reader = new FileReader();
    reader.onloadend = () => setEvidencePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const loadProductionCement = useCallback(async (dateKey) => {
    if (!dateKey) return;
    setLoadingProduction(true);
    try {
      const data = await apiJson(`/expenses/production-cement?date=${encodeURIComponent(dateKey)}`);
      setProductionCement(data);
    } catch {
      setProductionCement(null);
    } finally {
      setLoadingProduction(false);
    }
  }, []);

  useEffect(() => {
    if (!isDayWork) {
      setProductionCement(null);
      return;
    }
    void loadProductionCement(expenseDate);
  }, [isDayWork, expenseDate, loadProductionCement]);

  const pickDatePreset = (presetKey) => {
    setDatePreset(presetKey);
    setFieldErrors((prev) => ({ ...prev, expenseDate: undefined }));
    const preset = datePresets.find((p) => p.key === presetKey);
    if (preset?.value) {
      setExpenseDate(preset.value);
    }
  };

  const applyProductionBags = () => {
    const bags = productionCement?.cementBags;
    if (bags != null && Number(bags) > 0) {
      setCementBagsPay(String(bags));
      setFieldErrors((p) => ({ ...p, cementBagsPay: undefined }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!description.trim()) {
      errs.description = t('exp.err.description');
    }
    if (isStaffFactory && !staffPayType) {
      errs.staffPayType = t('exp.err.staffPayType');
    }
    if (isDayWork) {
      const bags = Number(cementBagsPay);
      const rate = Number(cementBagRate);
      if (!cementBagsPay || !Number.isFinite(bags) || bags <= 0) {
        errs.cementBagsPay = t('exp.err.cementBags');
      }
      if (!cementBagRate || !Number.isFinite(rate) || rate <= 0) {
        errs.cementBagRate = t('exp.err.cementRate');
      }
      if (dayWorkTotal < 0.01) {
        errs.amount = t('exp.err.amountCalc');
      }
    } else {
      const amt = Number(amount);
      if (!amount || !Number.isFinite(amt) || amt < 0.01) {
        errs.amount = t('exp.err.amount');
      }
    }
    if (!expenseDate || !/^\d{4}-\d{2}-\d{2}$/.test(expenseDate)) {
      errs.expenseDate = t('exp.err.date');
    }
    if (expenseDate > todayKey) {
      errs.expenseDate = t('exp.err.dateFuture');
    }
    if (!notes.trim()) {
      errs.notes = t('exp.err.notes');
    }
    if (paymentMethod === EXPENSE_PAYMENT_LIPA && !paymentReference.trim()) {
      errs.paymentReference = t('exp.err.lipaRef');
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const resetForm = () => {
    setDescription('');
    setAmount('');
    setNotes('');
    setSmsMessage('');
    resetEvidence();
    setPaymentMethod(EXPENSE_PAYMENT_CASH);
    setPaymentReference('');
    setStaffPayType(STAFF_PAY_PERMANENT);
    setCementBagsPay('');
    setCementBagRate('');
    setProductionCement(null);
    setDatePreset('today');
    setExpenseDate(todayKey);
    setFieldErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setMessage(null);
    if (!validate()) return;

    const finalAmount = isDayWork ? dayWorkTotal : Number(amount);

    setPhase('loading');
    try {
      const fd = new FormData();
      fd.append('category', category);
      if (isStaffFactory) fd.append('staffPayType', staffPayType);
      fd.append('description', description.trim());
      fd.append('amount', String(finalAmount));
      fd.append('expenseDate', expenseDate);
      fd.append('notes', notes.trim());
      fd.append('paymentMethod', paymentMethod);
      if (paymentMethod === EXPENSE_PAYMENT_LIPA && paymentReference.trim()) {
        fd.append('paymentReference', paymentReference.trim());
      }
      if (smsMessage.trim()) fd.append('smsMessage', smsMessage.trim());
      if (evidenceFile) fd.append('evidence', evidenceFile);
      if (isDayWork) {
        fd.append('cementBags', String(cementBagsPay));
        fd.append('cementBagRateTzs', String(cementBagRate));
        if (productionCement?.cementBags != null) {
          fd.append('productionCementBags', String(productionCement.cementBags));
        }
      }

      const res = await apiFetch('/expenses', { method: 'POST', body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(body.message || t('exp.err.save')), { body });
      resetForm();
      setPhase('idle');
      if (typeof onNavigate === 'function') {
        if (factory) {
          onNavigate('expenses-summary');
        } else if (category === EXPENSE_CATEGORY_VALUES.BOSS) {
          onNavigate('expenses-boss-history');
        } else if (category === EXPENSE_CATEGORY_VALUES.STAFF) {
          onNavigate('expenses-employee-history');
        } else {
          onNavigate('expenses-office-history');
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.body?.message || err.message || t('exp.err.save') });
      setPhase('idle');
    }
  };

  const ledeText = barMode
    ? t('exp.add.ledeBar')
    : factory
      ? t('exp.add.ledeFactory')
      : t('exp.add.ledeRetail');

  const descPlaceholder = isDayWork
    ? t('exp.ph.dayWorkDesc')
    : factory
      ? t('exp.ph.factoryDesc')
      : t('exp.ph.retailDesc');

  const notesPlaceholder = isDayWork
    ? t('exp.ph.dayWorkNotes')
    : factory
      ? t('exp.ph.factoryNotes')
      : t('exp.ph.retailNotes');

  const productionBatchSuffix =
    productionCement?.batchCount > 0
      ? t('exp.add.batchCount', { count: productionCement.batchCount })
      : t('exp.add.noBatch');

  return (
    <div className="expenses-shell expenses-shell--add expenses-page page-bleed">
      <header className="expenses-add-hero">
        <div className="expenses-add-hero__text">
          <h1 className="expenses-add-hero__title">{t('exp.add.title')}</h1>
          <p className="expenses-add-hero__lede">{ledeText}</p>
        </div>
        <span className="expenses-add-hero__badge" aria-hidden>
          TZS
        </span>
      </header>

      <div className="expenses-card expenses-card--add-form">
        {message ? (
          <p className="expenses-inline-msg expenses-inline-msg--err" role="alert">
            {message.text}
          </p>
        ) : null}

        <form className="expenses-add-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <section className="expenses-add-section">
            <h2 className="expenses-add-section__title">
              {t('exp.add.categoryTitle')} <span className="expenses-req">*</span>
            </h2>
            <div className="expenses-category-grid" role="radiogroup" aria-label="Category">
              {categoryOptions.map((o) => {
                const meta = CATEGORY_META[o.value] || { icon: '•', accent: 'other' };
                const selected = category === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    className={`expenses-category-chip expenses-category-chip--${meta.accent}${selected ? ' is-selected' : ''}`}
                    onClick={() => {
                      setCategory(o.value);
                      if (o.value !== EXPENSE_CATEGORY_VALUES.STAFF) {
                        setStaffPayType(STAFF_PAY_PERMANENT);
                        setCementBagsPay('');
                        setCementBagRate('');
                      }
                    }}
                    disabled={busy}
                    aria-pressed={selected}
                  >
                    <span className="expenses-category-chip__icon" aria-hidden>
                      {meta.icon}
                    </span>
                    <span className="expenses-category-chip__label">{o.label}</span>
                  </button>
                );
              })}
            </div>
            {categoryHint ? <p className="expenses-category-hint expenses-category-hint--card">{categoryHint}</p> : null}
          </section>

          {isStaffFactory ? (
            <section className="expenses-add-section">
              <h2 className="expenses-add-section__title">
                {t('exp.add.staffPayTitle')} <span className="expenses-req">*</span>
              </h2>
              <div className="expenses-staff-pay-grid" role="radiogroup" aria-label="Staff pay type">
                {payTypeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`expenses-staff-pay-card${staffPayType === opt.value ? ' is-selected' : ''}`}
                    onClick={() => {
                      setStaffPayType(opt.value);
                      setFieldErrors((p) => ({ ...p, staffPayType: undefined }));
                      if (opt.value === STAFF_PAY_PERMANENT) {
                        setCementBagsPay('');
                        setCementBagRate('');
                      }
                    }}
                    disabled={busy}
                    aria-pressed={staffPayType === opt.value}
                  >
                    <span className="expenses-staff-pay-card__icon" aria-hidden>
                      {opt.icon}
                    </span>
                    <span className="expenses-staff-pay-card__label">{opt.label}</span>
                    <span className="expenses-staff-pay-card__hint">{opt.hint}</span>
                  </button>
                ))}
              </div>
              {fieldErrors.staffPayType ? (
                <p className="expenses-field-error" role="alert">
                  {fieldErrors.staffPayType}
                </p>
              ) : null}
            </section>
          ) : null}

          <section className="expenses-add-section">
            <h2 className="expenses-add-section__title">
              {t('exp.add.dateTitle')} <span className="expenses-req">*</span>
            </h2>
            <div className="expenses-date-presets" role="group" aria-label="Quick date">
              {datePresets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`expenses-date-preset${datePreset === p.key ? ' is-active' : ''}`}
                  onClick={() => pickDatePreset(p.key)}
                  disabled={busy}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {datePreset === 'custom' ? (
              <label className="expenses-add-label expenses-add-label--inline" htmlFor="expense-date-custom">
                {t('exp.add.pickDate')}
                <input
                  id="expense-date-custom"
                  type="date"
                  className={`expenses-add-input${fieldErrors.expenseDate ? ' is-invalid' : ''}`}
                  value={expenseDate}
                  max={todayKey}
                  onChange={(e) => {
                    setExpenseDate(e.target.value);
                    setFieldErrors((p) => ({ ...p, expenseDate: undefined }));
                  }}
                  required
                  disabled={busy}
                />
              </label>
            ) : (
              <p className="expenses-date-selected">
                <span className="expenses-date-selected__label">{t('exp.add.dateSelected')}</span>
                <strong>{formatDateLabel(expenseDate, lang)}</strong>
              </p>
            )}
            {fieldErrors.expenseDate ? (
              <p className="expenses-field-error" role="alert">
                {fieldErrors.expenseDate}
              </p>
            ) : null}
          </section>

          {isDayWork ? (
            <section className="expenses-add-section expenses-daywork-panel">
              <h2 className="expenses-add-section__title">
                {t('exp.add.dayWorkTitle')} <span className="expenses-req">*</span>
              </h2>
              <div className="expenses-daywork-info">
                {loadingProduction ? (
                  <p className="expenses-daywork-info__text">{t('exp.add.loadingProduction')}</p>
                ) : productionCement ? (
                  <>
                    <p className="expenses-daywork-info__text">
                      {t('exp.add.productionLine', {
                        date: formatDateLabel(expenseDate, lang),
                        bags: productionCement.cementBags ?? 0,
                        batch: productionBatchSuffix,
                      })}
                    </p>
                    {Number(productionCement.cementBags) > 0 ? (
                      <button
                        type="button"
                        className="expenses-btn expenses-btn--secondary expenses-daywork-info__btn"
                        onClick={applyProductionBags}
                        disabled={busy}
                      >
                        {t('exp.add.useProductionBags')}
                      </button>
                    ) : null}
                  </>
                ) : (
                  <p className="expenses-daywork-info__text expenses-daywork-info__text--muted">
                    {t('exp.add.noProductionData')}
                  </p>
                )}
              </div>
              <div className="expenses-daywork-grid">
                <div className="expenses-add-section">
                  <label className="expenses-add-label" htmlFor="expense-cement-bags">
                    {t('exp.add.bagsToPay')} <span className="expenses-req">*</span>
                  </label>
                  <input
                    id="expense-cement-bags"
                    type="number"
                    min="0.01"
                    step="0.01"
                    className={`expenses-add-input${fieldErrors.cementBagsPay ? ' is-invalid' : ''}`}
                    value={cementBagsPay}
                    onChange={(e) => {
                      setCementBagsPay(e.target.value);
                      setFieldErrors((p) => ({ ...p, cementBagsPay: undefined }));
                    }}
                    disabled={busy}
                    placeholder="0"
                  />
                  {fieldErrors.cementBagsPay ? (
                    <p className="expenses-field-error" role="alert">
                      {fieldErrors.cementBagsPay}
                    </p>
                  ) : null}
                </div>
                <div className="expenses-add-section">
                  <label className="expenses-add-label" htmlFor="expense-cement-rate">
                    {t('exp.add.ratePerBag')} <span className="expenses-req">*</span>
                  </label>
                  <input
                    id="expense-cement-rate"
                    type="number"
                    min="1"
                    step="1"
                    className={`expenses-add-input${fieldErrors.cementBagRate ? ' is-invalid' : ''}`}
                    value={cementBagRate}
                    onChange={(e) => {
                      setCementBagRate(e.target.value);
                      setFieldErrors((p) => ({ ...p, cementBagRate: undefined }));
                    }}
                    disabled={busy}
                    placeholder="0"
                  />
                  {fieldErrors.cementBagRate ? (
                    <p className="expenses-field-error" role="alert">
                      {fieldErrors.cementBagRate}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="expenses-daywork-total">
                <span>{t('exp.add.payTotal')}</span>
                <strong>TZS {formatTzs(dayWorkTotal)}</strong>
                {cementBagsPay && cementBagRate ? (
                  <span className="expenses-daywork-total__formula">
                    {cementBagsPay} × {formatTzs(cementBagRate)}
                  </span>
                ) : null}
              </div>
            </section>
          ) : null}

          <div className="expenses-add-grid">
            <section className="expenses-add-section">
              <label className="expenses-add-label" htmlFor="expense-description">
                {isStaffFactory ? t('exp.add.descStaff') : t('exp.add.descOther')}{' '}
                <span className="expenses-req">*</span>
              </label>
              <input
                id="expense-description"
                type="text"
                className={`expenses-add-input${fieldErrors.description ? ' is-invalid' : ''}`}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setFieldErrors((p) => ({ ...p, description: undefined }));
                }}
                required
                disabled={busy}
                placeholder={descPlaceholder}
                autoComplete="off"
              />
              {fieldErrors.description ? (
                <p className="expenses-field-error" role="alert">
                  {fieldErrors.description}
                </p>
              ) : null}
            </section>

            {!isDayWork ? (
              <section className="expenses-add-section">
                <label className="expenses-add-label" htmlFor="expense-amount">
                  {t('exp.add.amount')} <span className="expenses-req">*</span>
                </label>
                <input
                  id="expense-amount"
                  type="number"
                  inputMode="decimal"
                  className={`expenses-add-input expenses-add-input--amount${fieldErrors.amount ? ' is-invalid' : ''}`}
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setFieldErrors((p) => ({ ...p, amount: undefined }));
                  }}
                  required
                  min="0.01"
                  step="1"
                  disabled={busy}
                  placeholder="0"
                />
                {fieldErrors.amount ? (
                  <p className="expenses-field-error" role="alert">
                    {fieldErrors.amount}
                  </p>
                ) : null}
              </section>
            ) : null}
          </div>

          <section className="expenses-add-section expenses-add-section--payment">
            <h2 className="expenses-add-section__title">
              {t('exp.add.paymentTitle')} <span className="expenses-req">*</span>
            </h2>
            <div className="expenses-payment-pills" role="radiogroup" aria-label="Payment method">
              {paymentOptions.map((o) => (
                <label
                  key={o.value}
                  className={`expenses-payment-pill${paymentMethod === o.value ? ' is-active' : ''}`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={o.value}
                    checked={paymentMethod === o.value}
                    onChange={() => {
                      setPaymentMethod(o.value);
                      if (o.value === EXPENSE_PAYMENT_CASH) {
                        setPaymentReference('');
                        setFieldErrors((p) => ({ ...p, paymentReference: undefined }));
                      }
                    }}
                    disabled={busy}
                  />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
            {paymentMethod === EXPENSE_PAYMENT_LIPA ? (
              <div className="expenses-lipa-block">
                <label className="expenses-add-label" htmlFor="expense-payment-ref">
                  {t('exp.add.lipaRef')} <span className="expenses-req">*</span>
                </label>
                <input
                  id="expense-payment-ref"
                  type="text"
                  className={`expenses-add-input${fieldErrors.paymentReference ? ' is-invalid' : ''}`}
                  value={paymentReference}
                  onChange={(e) => {
                    setPaymentReference(e.target.value);
                    setFieldErrors((p) => ({ ...p, paymentReference: undefined }));
                  }}
                  required
                  disabled={busy}
                  placeholder={t('exp.ph.lipaRef')}
                  autoComplete="off"
                />
                {fieldErrors.paymentReference ? (
                  <p className="expenses-field-error" role="alert">
                    {fieldErrors.paymentReference}
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="expenses-add-section expenses-add-section--optional">
            <h2 className="expenses-add-section__title">{t('exp.add.evidenceTitle')}</h2>
            <label className="expenses-add-label" htmlFor="expense-sms">
              {t('exp.add.sms')}
            </label>
            <textarea
              id="expense-sms"
              className="expenses-add-textarea expenses-add-textarea--sms"
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              rows={3}
              disabled={busy}
              placeholder={t('exp.ph.sms')}
            />
            <label className="expenses-add-label expenses-add-label--file" htmlFor="expense-evidence">
              {t('exp.add.receiptFile')}
            </label>
            <input
              key={fileKey}
              id="expense-evidence"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
              className="expenses-add-file"
              onChange={handleEvidenceChange}
              disabled={busy}
            />
            {evidencePreview && evidencePreviewKind === 'image' ? (
              <div className="expenses-evidence-preview">
                <img src={evidencePreview} alt={t('exp.add.previewAlt')} />
                <button type="button" className="expenses-evidence-remove" onClick={resetEvidence} disabled={busy}>
                  {t('exp.add.remove')}
                </button>
              </div>
            ) : null}
            {evidencePreview && evidencePreviewKind === 'pdf' ? (
              <div className="expenses-evidence-preview expenses-evidence-preview--pdf">
                <span>{t('exp.add.pdfSelected', { name: evidenceFile?.name })}</span>
                <button type="button" className="expenses-evidence-remove" onClick={resetEvidence} disabled={busy}>
                  {t('exp.add.remove')}
                </button>
              </div>
            ) : null}
          </section>

          <section className="expenses-add-section">
            <label className="expenses-add-label" htmlFor="expense-notes">
              {t('exp.add.notes')} <span className="expenses-req">*</span>
            </label>
            <textarea
              id="expense-notes"
              className={`expenses-add-textarea${fieldErrors.notes ? ' is-invalid' : ''}`}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setFieldErrors((p) => ({ ...p, notes: undefined }));
              }}
              rows={3}
              required
              disabled={busy}
              placeholder={notesPlaceholder}
            />
            {fieldErrors.notes ? (
              <p className="expenses-field-error" role="alert">
                {fieldErrors.notes}
              </p>
            ) : null}
          </section>

          <button type="submit" className="expenses-add-submit" disabled={busy}>
            {busy ? t('exp.add.saving') : t('exp.add.save')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ExpensesAdd;
